/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Driver } from 'types/entities/Driver'
import { Reservation } from 'types/entities/Reservation'
import { ReservationNote } from 'store/booking/BookingAction'
import { ReservationDriver } from 'types/entities/ReservationDriver'
import { PackagesCartItem } from 'types/entities/PackagesCartItem'

export interface BookingLoginReducerState {
    reservations: Reservation[]
    selectedReservation: Reservation | null
    user: Driver | null
}

const initialState: BookingLoginReducerState = {
    reservations: [],
    selectedReservation: null,
    user: null,
}

const bookingLoginSlice = createSlice({
    name: 'bookingLogin',
    initialState,
    reducers: {
        setReservations(state, action: PayloadAction<Reservation[]>) {
            state.reservations = action.payload
        },
        setUser(state, action: PayloadAction<Driver | null>) {
            state.user = action.payload
        },
        clearReservations(state, action: PayloadAction<Reservation[]>) {
            state.reservations = action.payload
        },
        pushReservationNote(state, action: PayloadAction<ReservationNote>) {
            const reservationIndex = state.reservations.findIndex(
                (r) => r.id === action.payload.reservation_id
            )
            if (reservationIndex === -1) {
                return
            }
            const noteIndex = state.reservations[
                reservationIndex
            ].reservationNotes.findIndex((note) => note.id === action.payload.id)
            if (noteIndex === -1) {
                state.reservations[reservationIndex].reservationNotes.push({
                    ...action.payload
                })
            } else {
                state.reservations[reservationIndex].reservationNotes[noteIndex] = {
                    ...action.payload
                }
            }
        },
        setReservation(state, action: PayloadAction<Reservation>) {
            const index = state.reservations.findIndex(
                (r) => r.id === action.payload.id
            )
            if (index === -1) {
                state.reservations.push(action.payload)
            } else {
                state.reservations[index] = action.payload
            }
            state.selectedReservation = action.payload
        },
        setSelectedReservationPackagesCartItem(
            state,
            action: PayloadAction<PackagesCartItem>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.packagesCart ||
                !state.selectedReservation.packagesCart.packagesCartItems
            ) {
                return
            }
            const index =
                state.selectedReservation.packagesCart.packagesCartItems.findIndex(
                    (item) => item.id === action.payload.id
                )
            if (index === -1) {
                state.selectedReservation.packagesCart.packagesCartItems.push(
                    action.payload
                )
            } else {
                state.selectedReservation.packagesCart.packagesCartItems[index] =
                    action.payload
            }
        },
        setSelectedReservationReservationDriver(
            state,
            action: PayloadAction<ReservationDriver>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.reservationDrivers
            ) {
                return
            }
            const index = state.selectedReservation.reservationDrivers.findIndex(
                (driver) => driver.id === action.payload.id
            )
            if (index === -1) {
                state.selectedReservation.reservationDrivers.push(action.payload)
            } else {
                state.selectedReservation.reservationDrivers[index] = action.payload
            }
        },
        removeReservationDriverFromSelectedReservation(
            state,
            action: PayloadAction<number>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.reservationDrivers
            ) {
                return
            }
            state.selectedReservation.reservationDrivers =
                state.selectedReservation.reservationDrivers.filter(
                    (driver) => driver.id !== action.payload
                )
        },
        removePackagesCartItemFromSelectedReservation(
            state,
            action: PayloadAction<number>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.packagesCart ||
                !state.selectedReservation.packagesCart.packagesCartItems
            ) {
                return
            }
            const index =
                state.selectedReservation.packagesCart.packagesCartItems.findIndex(
                    (item) => item.id === action.payload
                )
            if (index !== -1) {
                state.selectedReservation.packagesCart.packagesCartItems[
                    index
                ].reservation_driver_id = null
            }
        },
        removeReservationFromList(state, action: PayloadAction<Reservation>) {
            state.reservations = state.reservations.filter(
                (r) => r.id !== action.payload.id
            )
        },
        setDriverFamilyAccounts(state, action: PayloadAction<Driver>) {
            if (!state.user || !state.user.familyAccounts) {
                return
            }
            const index = state.user.familyAccounts.findIndex(
                (driver) => driver.id === action.payload.id
            )
            if (index === -1) {
                state.user.familyAccounts.push(action.payload)
            } else {
                state.user.familyAccounts[index] = action.payload
            }
        },
        reset(state) {
            return initialState
        },
        resetState: () => initialState,
    },
})

export const {
    setReservations,
    setUser,
    clearReservations,
    pushReservationNote,
    setReservation,
    setSelectedReservationPackagesCartItem,
    setSelectedReservationReservationDriver,
    removeReservationDriverFromSelectedReservation,
    removePackagesCartItemFromSelectedReservation,
    removeReservationFromList,
    setDriverFamilyAccounts,
    reset,
    resetState,
} = bookingLoginSlice.actions

export default bookingLoginSlice.reducer
